import { IAddHouseholdMemberRequest } from '@/shared/interfaces';
import { Modal } from '@/components';
import { R } from '../ProfileResources.resx';
import { R as GlobalResources } from '@/Resources.resx';
import { Button } from '@/components/Button';
import { useForm } from 'react-hook-form';
import {
    isEmail,
    isFullName,
    isCprNumber,
    isUnderCertainAge,
} from '@/shared/validators';
import { FormErrorText } from '@/components/FormErrorText';
import { useMutation } from '@tanstack/react-query';
import { Input } from '@/components/Input';
import * as React from 'react';
import { Utils } from '@/shared/Utils';
import { useEffect, useState } from 'react';
import { ModulusWarning } from '@/features/profile/components/ModulusWarning';
import { addHouseholdMember } from '@/api/profile/addHouseholdMember';

type AddHouseholdMemberModalProps = {
    onClose: (reload: boolean) => void;
};

export const AddHouseholdMemberModal = ({
    onClose,
}: AddHouseholdMemberModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm<IAddHouseholdMemberRequest>({ mode: 'onBlur' });

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const addHouseholdMemberMutation = useMutation({
        mutationFn: addHouseholdMember,
        onSuccess: () => onClose(true),
        onError: (error) => {
            setErrorMessage(error.message);
        },
    });

    const [showModulusWarning, setShowModulusWarning] = useState(false);

    useEffect(() => {
        setErrorMessage(null);
    }, [showModulusWarning]);

    const onSubmit = async () => {
        if (addHouseholdMemberMutation.isPending) return;

        const data = getValues();

        if (!Utils.cprModulusCheck(data.Cpr) && !showModulusWarning) {
            setShowModulusWarning(true);
            return;
        }

        addHouseholdMemberMutation.mutate(data);
    };

    const onCancel = () => onClose(false);

    return (
        <Modal onClose={onCancel} title={R.InvitePartner}>
            {showModulusWarning && (
                <ModulusWarning
                    onBack={() => setShowModulusWarning(false)}
                    onConfirm={onSubmit}
                    cprNumber={getValues().Cpr}
                    isPending={addHouseholdMemberMutation.isPending}
                    errorMessage={errorMessage}
                />
            )}

            {!showModulusWarning && (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <main className="py-6">
                        <Input
                            label={R.Name}
                            inputClassName="mb-3"
                            name={`add-household-member-modal-${R.Name}`}
                            {...register('Name', {
                                validate: {
                                    isFullName: (value: string) =>
                                        isFullName(value) ||
                                        R.PartnerNameRequired,
                                },
                            })}
                            error={errors.Name?.message}
                        />

                        <Input
                            label={R.CprNumber}
                            inputClassName="mb-3"
                            name={`add-household-member-modal-${R.CprNumber}`}
                            {...register('Cpr', {
                                required: R.PartnerCprRequired,
                                setValueAs: (value) => value.replace('-', ''),
                                validate: {
                                    validCpr: (value: string) =>
                                        isCprNumber(value) || R.InvalidCpr,
                                    isOverCertainAge: (value: string) =>
                                        !isUnderCertainAge(value, 18) ||
                                        GlobalResources.CprTooYoung,
                                },
                            })}
                            error={errors.Cpr?.message}
                            maxLength={11}
                            onInput={Utils.sanitizeCprInput}
                        />

                        <Input
                            label={R.Email}
                            inputClassName="mb-3"
                            name={`add-household-member-modal-${R.Email}`}
                            {...register('Email', {
                                required: R.PartnerEmailRequired,
                                validate: {
                                    isEmail: (value: string) =>
                                        isEmail(value) || R.InvalidEmail,
                                },
                            })}
                            error={errors.Email?.message}
                        />

                        <FormErrorText message={errorMessage ?? ''} />
                    </main>

                    <footer className="text-right">
                        <Button
                            variant="primary"
                            type="submit"
                            className={
                                addHouseholdMemberMutation.isPending
                                    ? 'button--loading'
                                    : ''
                            }
                        >
                            {R.InvitePartner}
                        </Button>
                    </footer>
                </form>
            )}
        </Modal>
    );
};
